<script>
import Layout from "../../layouts/main";
import PageHeader from "../../components/page-header";
import Swal from "sweetalert2";
import Multiselect from 'vue-multiselect';
import * as XLSX from "xlsx";

export default {
    components: { Layout, PageHeader, Multiselect },
    data() {
        return {
            title: "Descargar colegios",
            items: [
                {
                    text: "Configuración",
                    href: "/"
                },
                {
                    text: "Colegios",
                    href: "/web/configuracion/colegios"
                },
                {
                    text: "Descargar colegios",
                    active: true
                }
            ],
            isLoading: false,
            exportando: false,
            colegios: [],
            estados: [],
            filtros: {
                busqueda: '',
                estado: null,
                segmentacion: null,
                comuna: null,
                region: null,
                sede: null,
                propietario: null,
                visitado: null,
            },
            columnasPreferidas: [
                'id',
                'rbd',
                'nombre',
                'estado_nombre',
                'segmentacion',
                'colegio_visitado',
                'propietario',
                'sede_asociada',
                'direccion',
                'comuna',
                'provincia',
                'region',
                'telefono',
                'telefono_colegio',
                'correo_colegio',
                'nombre_director',
                'telefono_director',
                'nombre_orientador',
                'correo_colegio_2',
                'telefono_orientador',
                'cluster',
            ],
            columnasExcluidas: new Set([
                'estado_colegio',
                'user_id',
                'campus',
                'tipo_educacion',
                'nivel_socioeconomico',
                'cantidad_encuestas',
                'created_at',
                'updated_at',
                'data',
                'revisado',
                'valido',
            ]),
        };
    },
    computed: {
        estadosOptions() {
            return this.estados.map((estado) => ({
                value: estado.codigo,
                label: estado.nombre,
            }));
        },
        comunasOptions() {
            return this.buildUniqueOptions('comuna');
        },
        segmentacionesOptions() {
            return this.buildUniqueOptions('segmentacion');
        },
        regionesOptions() {
            return this.buildUniqueOptions('region');
        },
        sedesOptions() {
            return this.buildUniqueOptions('sede_asociada');
        },
        propietariosOptions() {
            return this.buildUniqueOptions('propietario');
        },
        visitadoOptions() {
            return [
                { value: 'Sí', label: 'Sí' },
                { value: 'No', label: 'No' },
            ];
        },
        colegiosFiltrados() {
            const busqueda = this.normalizeText(this.filtros.busqueda);

            return this.colegios.filter((colegio) => {
                if (busqueda) {
                    const contenido = this.normalizeText([
                        colegio.rbd,
                        colegio.nombre,
                        colegio.segmentacion,
                        colegio.comuna,
                        colegio.region,
                        colegio.sede_asociada,
                        colegio.propietario,
                        colegio.estado_nombre,
                        colegio.nombre_director,
                        colegio.nombre_orientador,
                    ].join(' '));

                    if (!contenido.includes(busqueda)) {
                        return false;
                    }
                }

                if (this.filtros.estado && colegio.estado_colegio !== this.filtros.estado.value) {
                    return false;
                }

                if (this.filtros.segmentacion && this.normalizeText(colegio.segmentacion) !== this.filtros.segmentacion.value) {
                    return false;
                }

                if (this.filtros.comuna && this.normalizeText(colegio.comuna) !== this.filtros.comuna.value) {
                    return false;
                }

                if (this.filtros.region && this.normalizeText(colegio.region) !== this.filtros.region.value) {
                    return false;
                }

                if (this.filtros.sede && this.normalizeText(colegio.sede_asociada) !== this.filtros.sede.value) {
                    return false;
                }

                if (this.filtros.propietario && this.normalizeText(colegio.propietario) !== this.filtros.propietario.value) {
                    return false;
                }

                if (this.filtros.visitado && colegio.colegio_visitado !== this.filtros.visitado.value) {
                    return false;
                }

                return true;
            });
        },
        cantidadResultados() {
            return this.colegiosFiltrados.length;
        },
        previewRows() {
            return this.colegiosFiltrados.slice(0, 150);
        },
    },
    mounted() {
        this.cargarDatos();
    },
    methods: {
        msg(tipo, titulo) {
            Swal.fire({
                icon: tipo,
                title: titulo,
                showConfirmButton: false,
                timer: 3500,
            });
        },
        normalizeText(value) {
            return String(value || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .trim();
        },
        getCanonicalLabel(field, rawValue, normalizedValue) {
            if (!normalizedValue) {
                return '';
            }

            const canonicalByField = {
                sede_asociada: {
                    concepcion: 'Concepción',
                    santiago: 'Santiago',
                    'vina del mar': 'Viña del Mar',
                },
                comuna: {
                    concepcion: 'Concepción',
                    santiago: 'Santiago',
                    'vina del mar': 'Viña del Mar',
                },
                region: {
                    'metropolitana de santiago': 'Metropolitana de Santiago',
                    biobio: 'Biobío',
                    valparaiso: 'Valparaíso',
                    nuble: 'Ñuble',
                    araucania: 'Araucanía',
                    'ohiggins': "O'Higgins",
                    'los rios': 'Los Ríos',
                    'los lagos': 'Los Lagos',
                },
            };

            const canonical = canonicalByField[field] && canonicalByField[field][normalizedValue];
            return canonical || rawValue;
        },
        buildUniqueOptions(field) {
            const unique = new Map();

            this.colegios.forEach((colegio) => {
                const rawValue = (colegio[field] || '').toString().trim();
                const normalizedValue = this.normalizeText(rawValue);

                if (!normalizedValue) {
                    return;
                }

                if (!unique.has(normalizedValue)) {
                    unique.set(normalizedValue, this.getCanonicalLabel(field, rawValue, normalizedValue));
                }
            });

            return Array.from(unique.entries())
                .map(([value, label]) => ({ value, label }))
                .sort((left, right) => left.label.localeCompare(right.label, 'es'));
        },
        async cargarDatos() {
            this.isLoading = true;
            try {
                const [colegiosResponse, estadosResponse] = await Promise.all([
                    axios.get('/api/get_colegios'),
                    axios.get('/api/get_estado_colegios'),
                ]);

                this.estados = Array.isArray(estadosResponse.data.estados)
                    ? estadosResponse.data.estados
                    : [];

                const estadosPorCodigo = this.estados.reduce((accumulator, estado) => {
                    accumulator[String(estado.codigo)] = estado.nombre;
                    return accumulator;
                }, {});

                const colegios = Array.isArray(colegiosResponse.data.colegios)
                    ? colegiosResponse.data.colegios
                    : [];

                this.colegios = colegios.map((colegio) => {
                    const estadoCodigo = colegio.estado_colegio != null ? String(colegio.estado_colegio) : '';
                    return {
                        ...colegio,
                        estado_colegio: estadoCodigo,
                        estado_nombre: estadosPorCodigo[estadoCodigo] || '',
                        telefono_colegio: colegio.telefono_colegio || colegio.telefono || '',
                    };
                });
            } catch (error) {
                this.msg('error', 'No se pudo cargar el listado de colegios');
            } finally {
                this.isLoading = false;
            }
        },
        limpiarFiltros() {
            this.filtros = {
                busqueda: '',
                estado: null,
                segmentacion: null,
                comuna: null,
                region: null,
                sede: null,
                propietario: null,
                visitado: null,
            };
        },
        valorExcel(value) {
            if (value === null || value === undefined) {
                return '';
            }

            if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
                try {
                    return JSON.stringify(value, null, 0);
                } catch (error) {
                    return String(value);
                }
            }

            return value;
        },
        buildExcelRows(source) {
            const rows = Array.isArray(source) ? source : [];
            const dynamicColumns = new Set();

            rows.forEach((row) => {
                Object.keys(row || {}).forEach((key) => {
                    if (!this.columnasExcluidas.has(key)) {
                        dynamicColumns.add(key);
                    }
                });
            });

            const orderedColumns = [
                ...this.columnasPreferidas.filter((column) => dynamicColumns.has(column)),
                ...Array.from(dynamicColumns).filter((column) => !this.columnasPreferidas.includes(column)).sort(),
            ];

            return rows.map((row) => {
                const exportRow = {};
                orderedColumns.forEach((column) => {
                    exportRow[column] = this.valorExcel(row[column]);
                });
                return exportRow;
            });
        },
        descargarExcel(source, nombreBase) {
            const rows = this.buildExcelRows(source);

            if (!rows.length) {
                this.msg('warning', 'No hay colegios para descargar con los filtros actuales');
                return;
            }

            this.exportando = true;
            try {
                const worksheet = XLSX.utils.json_to_sheet(rows);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Colegios');
                XLSX.writeFile(workbook, `${nombreBase}_${new Date().toISOString().slice(0, 10)}.xlsx`);
                this.msg('success', 'Excel generado correctamente');
            } catch (error) {
                this.msg('error', 'No se pudo generar el Excel');
            } finally {
                this.exportando = false;
            }
        },
        descargarFiltrados() {
            this.descargarExcel(this.colegiosFiltrados, 'colegios_filtrados');
        },
        descargarTodos() {
            this.descargarExcel(this.colegios, 'colegios_completo');
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
                            <div>
                                <h4 class="card-title mb-1">Filtros de colegios</h4>
                                <p class="text-muted mb-0">Aplica filtros antes de descargar el listado en Excel.</p>
                            </div>
                            <div class="d-flex flex-wrap mt-3 mt-md-0">
                                <b-button variant="light" class="mr-2 mb-2" @click="limpiarFiltros">
                                    Limpiar filtros
                                </b-button>
                                <b-button variant="primary" class="mr-2 mb-2" :disabled="exportando || !cantidadResultados" @click="descargarFiltrados">
                                    Descargar filtrados
                                </b-button>
                                <b-button variant="success" class="mb-2" :disabled="exportando || !colegios.length" @click="descargarTodos">
                                    Descargar todo
                                </b-button>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-md-4 mb-3">
                                <label>Buscar colegio</label>
                                <b-form-input v-model.trim="filtros.busqueda" placeholder="RBD, nombre, comuna, propietario..."></b-form-input>
                            </div>
                            <div class="col-md-4 mb-3">
                                <label>Estado</label>
                                <multiselect
                                    v-model="filtros.estado"
                                    :options="estadosOptions"
                                    label="label"
                                    track-by="value"
                                    :show-labels="false"
                                    placeholder="Todos los estados"
                                ></multiselect>
                            </div>
                            <div class="col-md-4 mb-3">
                                <label>Comuna</label>
                                <multiselect
                                    v-model="filtros.comuna"
                                    :options="comunasOptions"
                                    label="label"
                                    track-by="value"
                                    :show-labels="false"
                                    placeholder="Todas las comunas"
                                ></multiselect>
                            </div>
                            <div class="col-md-4 mb-3">
                                <label>Segmentación</label>
                                <multiselect
                                    v-model="filtros.segmentacion"
                                    :options="segmentacionesOptions"
                                    label="label"
                                    track-by="value"
                                    :show-labels="false"
                                    placeholder="Todas las segmentaciones"
                                ></multiselect>
                            </div>
                            <div class="col-md-4 mb-3">
                                <label>Región</label>
                                <multiselect
                                    v-model="filtros.region"
                                    :options="regionesOptions"
                                    label="label"
                                    track-by="value"
                                    :show-labels="false"
                                    placeholder="Todas las regiones"
                                ></multiselect>
                            </div>
                            <div class="col-md-4 mb-3">
                                <label>Sede asociada</label>
                                <multiselect
                                    v-model="filtros.sede"
                                    :options="sedesOptions"
                                    label="label"
                                    track-by="value"
                                    :show-labels="false"
                                    placeholder="Todas las sedes"
                                ></multiselect>
                            </div>
                            <div class="col-md-4 mb-3">
                                <label>Propietario</label>
                                <multiselect
                                    v-model="filtros.propietario"
                                    :options="propietariosOptions"
                                    label="label"
                                    track-by="value"
                                    :show-labels="false"
                                    placeholder="Todos los propietarios"
                                ></multiselect>
                            </div>
                            <div class="col-md-4 mb-3">
                                <label>Colegio visitado</label>
                                <multiselect
                                    v-model="filtros.visitado"
                                    :options="visitadoOptions"
                                    label="label"
                                    track-by="value"
                                    :show-labels="false"
                                    placeholder="Sí o No"
                                ></multiselect>
                            </div>
                        </div>

                        <div class="alert alert-info d-flex justify-content-between align-items-center mb-0">
                            <span><strong>{{ cantidadResultados }}</strong> colegios coinciden con los filtros actuales.</span>
                            <span v-if="cantidadResultados > previewRows.length">Mostrando vista previa de {{ previewRows.length }} registros.</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <h4 class="card-title mb-3">Vista previa del listado</h4>
                        <div v-if="isLoading" class="text-center text-muted py-5">
                            Cargando colegios...
                        </div>
                        <div v-else-if="!previewRows.length" class="text-center text-muted py-5">
                            No hay colegios que coincidan con los filtros seleccionados.
                        </div>
                        <div v-else class="table-responsive">
                            <table class="table table-bordered table-hover align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>RBD</th>
                                        <th>Nombre</th>
                                        <th>Estado</th>
                                        <th>Segmentación</th>
                                        <th>Comuna</th>
                                        <th>Región</th>
                                        <th>Sede</th>
                                        <th>Propietario</th>
                                        <th>Visitado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="colegio in previewRows" :key="colegio.id">
                                        <td>{{ colegio.rbd }}</td>
                                        <td>{{ colegio.nombre }}</td>
                                        <td>{{ colegio.estado_nombre || 'Sin estado' }}</td>
                                        <td>{{ colegio.segmentacion || 'Sin segmentación' }}</td>
                                        <td>{{ colegio.comuna }}</td>
                                        <td>{{ colegio.region }}</td>
                                        <td>{{ colegio.sede_asociada }}</td>
                                        <td>{{ colegio.propietario }}</td>
                                        <td>{{ colegio.colegio_visitado }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>
